import { useEffect, useMemo, useRef, useState } from "react";
import SEO from "../../components/SEO";
import { faqCategories, faqs } from "../../constants/faqs";
import { BsArrowRight, BsCheck2, BsChevronDown, BsEnvelope, BsLink45Deg, BsSearch, BsWhatsapp, BsXLg } from "react-icons/bs";

function Highlight({ text, term }) {
	if (!term) return text;
	const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
	return parts.map((part, index) =>
		part.toLowerCase() === term.toLowerCase() ? (
			<mark key={index} className="bg-amber-200/80 text-gray-900 rounded px-0.5">{part}</mark>
		) : (
			part
		),
	);
}

function FAQItem({ faq, term, isOpen, onToggle, onCopyLink, copied }) {
	return (
		<div id={faq.id} className={`scroll-mt-28 rounded-2xl border bg-white transition-all duration-300 ${isOpen ? "border-purple-200 shadow-md" : "border-gray-100 shadow-sm hover:border-purple-200 hover:shadow-md"}`}>
			<h3>
				<button
					type="button"
					onClick={onToggle}
					aria-expanded={isOpen}
					aria-controls={`${faq.id}-answer`}
					className="group w-full flex items-center gap-4 text-left px-5 py-4 md:px-6"
				>
					<span className="flex-1 text-base md:text-lg font-semibold text-gray-900">
						<Highlight text={faq.question} term={term} />
					</span>
					<span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ${isOpen ? "bg-purple-600 text-white rotate-180" : "bg-purple-50 text-purple-600 group-hover:bg-purple-100"}`}>
						<BsChevronDown aria-hidden="true" />
					</span>
				</button>
			</h3>
			<div
				id={`${faq.id}-answer`}
				role="region"
				className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
			>
				<div className="overflow-hidden">
					<div className="px-5 pb-5 md:px-6 text-gray-600 leading-relaxed">
						<p><Highlight text={faq.answer} term={term} /></p>
						<div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
							{faq.links?.map((link) => (
								<a key={link.href + link.label} href={link.href} className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-700 hover:text-purple-900">
									{link.label} <BsArrowRight aria-hidden="true" />
								</a>
							))}
							<button
								type="button"
								onClick={onCopyLink}
								className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-purple-700"
							>
								{copied ? <BsCheck2 aria-hidden="true" /> : <BsLink45Deg aria-hidden="true" />}
								{copied ? "Link copied" : "Copy link"}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export const FAQPage = () => {
	const [query, setQuery] = useState("");
	const [category, setCategory] = useState("all");
	const [openIds, setOpenIds] = useState(() => new Set());
	const [copiedId, setCopiedId] = useState(null);
	const copiedTimer = useRef(null);
	const term = query.trim();

	const matches = useMemo(() => {
		const lowered = term.toLowerCase();
		return faqs.filter(
			(faq) =>
				(category === "all" || faq.category === category) &&
				(!lowered || faq.question.toLowerCase().includes(lowered) || faq.answer.toLowerCase().includes(lowered)),
		);
	}, [term, category]);

	// Open a question shared by link (e.g. /faq#insurance).
	useEffect(() => {
		const sharedId = window.location.hash.slice(1);
		if (!faqs.some((faq) => faq.id === sharedId)) return;
		setOpenIds(new Set([sharedId]));
		requestAnimationFrame(() => document.getElementById(sharedId)?.scrollIntoView({ behavior: "smooth", block: "start" }));
	}, []);

	// While searching, show matching answers so the highlights are visible.
	useEffect(() => {
		if (term) setOpenIds(new Set(matches.map((faq) => faq.id)));
	}, [term, matches]);

	useEffect(() => () => clearTimeout(copiedTimer.current), []);

	const toggle = (id) =>
		setOpenIds((previous) => {
			const next = new Set(previous);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	const copyLink = (id) => {
		const url = `${window.location.origin}/faq#${id}`;
		navigator.clipboard?.writeText(url).catch(() => {});
		window.history.replaceState(null, "", `#${id}`);
		setCopiedId(id);
		clearTimeout(copiedTimer.current);
		copiedTimer.current = setTimeout(() => setCopiedId(null), 2000);
	};

	const countFor = (key) => (key === "all" ? faqs.length : faqs.filter((faq) => faq.category === key).length);
	const groups = faqCategories
		.map((group) => ({ ...group, items: matches.filter((faq) => faq.category === group.key) }))
		.filter((group) => group.items.length > 0);

	return (
		<div className="w-full min-h-screen overflow-x-hidden bg-gradient-to-br from-cyan-100 to-fuchsia-200">
			<SEO title="FAQ" description="Answers to common questions about Parcelin: delivery areas, pricing, payment, tracking, insurance, business accounts and more." path="/faq" />

			<section className="relative">
				<div className="pointer-events-none absolute -top-24 -right-20 h-72 w-72 rounded-full bg-fuchsia-300/40 blur-3xl motion-safe:animate-pulse" aria-hidden="true" />
				<div className="pointer-events-none absolute top-32 -left-24 h-64 w-64 rounded-full bg-cyan-300/40 blur-3xl motion-safe:animate-pulse" aria-hidden="true" />
				<div className="relative max-w-3xl mx-auto px-4 sm:px-5 pt-14 md:pt-20 pb-8 text-center">
					<p className="text-sm font-medium uppercase tracking-widest text-purple-600 mb-3">Help centre</p>
					<h1 className="text-4xl md:text-5xl text-purple-800 mb-4">How can we help?</h1>
					<p className="text-lg text-gray-700 mb-8">Search our answers or browse by topic.</p>
					<div className="relative max-w-xl mx-auto">
						<BsSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-purple-500 text-lg" aria-hidden="true" />
						<input
							type="search"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Try “tracking” or “payment”"
							aria-label="Search frequently asked questions"
							className="w-full pl-12 pr-12 py-4 bg-white rounded-full border-2 border-white shadow-lg text-base focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-200 transition-shadow"
						/>
						{query && (
							<button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="absolute right-4 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600">
								<BsXLg aria-hidden="true" />
							</button>
						)}
					</div>
					<p className="mt-3 text-sm text-gray-600" aria-live="polite">
						{term ? `${matches.length} ${matches.length === 1 ? "answer" : "answers"} for “${term}”` : `${faqs.length} answers to common questions`}
					</p>
				</div>
			</section>

			<div className="max-w-3xl mx-auto px-4 sm:px-5 pb-16">
				<div role="tablist" aria-label="FAQ topics" className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1 sm:flex-wrap sm:justify-center">
					{[{ key: "all", label: "All topics" }, ...faqCategories].map((topic) => (
						<button
							key={topic.key}
							type="button"
							role="tab"
							aria-selected={category === topic.key}
							onClick={() => setCategory(topic.key)}
							className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all ${
								category === topic.key ? "bg-purple-600 text-white shadow-md scale-105" : "bg-white/80 text-gray-700 hover:bg-white hover:text-purple-700"
							}`}
						>
							{topic.label}
							<span className={`text-xs rounded-full px-1.5 py-0.5 ${category === topic.key ? "bg-white/25" : "bg-purple-50 text-purple-700"}`}>{countFor(topic.key)}</span>
						</button>
					))}
				</div>

				{groups.length === 0 ? (
					<div className="bg-white rounded-2xl border border-purple-100 shadow-sm p-8 text-center motion-safe:animate-fadeUp">
						<h2 className="text-xl text-gray-900 mb-2">No answers for “{term}”</h2>
						<p className="text-gray-600 mb-6">Ask us directly and we'll get back to you.</p>
						<div className="flex flex-wrap justify-center gap-3">
							<a href="https://wa.me/27699042846" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2.5 rounded-full">
								<BsWhatsapp aria-hidden="true" /> Ask on WhatsApp
							</a>
							<button type="button" onClick={() => setQuery("")} className="inline-flex items-center gap-2 bg-white hover:bg-purple-50 text-purple-700 font-semibold px-5 py-2.5 rounded-full border border-purple-200">
								Clear search
							</button>
						</div>
					</div>
				) : (
					<div key={category} className="space-y-8">
						{groups.map((group, groupIndex) => (
							<section key={group.key} aria-labelledby={`faq-${group.key}`}>
								<h2 id={`faq-${group.key}`} className="text-sm uppercase tracking-widest text-purple-700 mb-3">
									{group.label}
								</h2>
								<div className="space-y-3">
									{group.items.map((faq, index) => (
										<div key={faq.id} className="motion-safe:animate-fadeUp" style={{ animationDelay: `${(groupIndex * 3 + index) * 40}ms` }}>
											<FAQItem
												faq={faq}
												term={term}
												isOpen={openIds.has(faq.id)}
												onToggle={() => toggle(faq.id)}
												onCopyLink={() => copyLink(faq.id)}
												copied={copiedId === faq.id}
											/>
										</div>
									))}
								</div>
							</section>
						))}
					</div>
				)}

				<div className="mt-12 rounded-3xl bg-gradient-to-br from-purple-700 to-fuchsia-600 text-white p-8 md:p-10 text-center">
					<h2 className="text-2xl md:text-3xl mb-2">Still have questions?</h2>
					<p className="text-white/85 mb-6">Our support team is happy to help.</p>
					<div className="flex flex-wrap justify-center gap-3">
						<a href="https://wa.me/27699042846" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-purple-700 hover:bg-purple-50 font-semibold px-6 py-3 rounded-full transition-colors">
							<BsWhatsapp aria-hidden="true" /> WhatsApp Us
						</a>
						<a href="/contact" className="inline-flex items-center gap-2 border border-white/60 hover:bg-white/10 font-semibold px-6 py-3 rounded-full transition-colors">
							<BsEnvelope aria-hidden="true" /> Contact Us
						</a>
					</div>
				</div>
			</div>
		</div>
	);
};
